import axios from 'axios'
import {
  User,
  Reservation,
  CreateReservationDto,
  UpdateReservationDto,
  LoginDto,
  RegisterDto,
  AuthResponse,
  UserSubscription
} from '../types'

// In production, prefer same-origin by default. If VITE_API_URL is set, use it.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined) || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/auth/refresh`, {
            refreshToken,
          })
          
          const { accessToken } = response.data
          localStorage.setItem('accessToken', accessToken)
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Auth endpoints
export const authApi = {
  login: async (data: LoginDto): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', data)
    return response.data
  },

  register: async (data: RegisterDto): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', data)
    return response.data
  },

  refresh: async (refreshToken: string): Promise<{ accessToken: string }> => {
    const response = await api.post('/auth/refresh', { refreshToken })
    return response.data
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout')
  },

  me: async (): Promise<User> => {
    const response = await api.get('/auth/me')
    return response.data
  },
}

// Users endpoints
export const usersApi = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users')
    return response.data
  },

  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`)
    return response.data
  },

  create: async (data: RegisterDto): Promise<User> => {
    const response = await api.post('/users', data)
    return response.data
  },

  update: async (id: string, data: Partial<User>): Promise<User> => {
    const response = await api.patch(`/users/${id}`, data)
    return response.data
  },

  approve: async (id: string): Promise<User> => {
    const response = await api.post(`/users/${id}/approve`)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`)
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.post('/users/change-password', { currentPassword, newPassword })
    return response.data
  },
}

// Calendar links type
export interface CalendarLinks {
  googleCalendarUrl: string
  icsUrl: string
  outlookUrl: string
}

// Reservations endpoints
export const reservationsApi = {
  getAll: async (): Promise<Reservation[]> => {
    const response = await api.get('/reservations')
    return response.data
  },

  getWithSubscriptions: async (): Promise<Reservation[]> => {
    const response = await api.get('/reservations/subscribed')
    return response.data
  },

  getById: async (id: string): Promise<Reservation> => {
    const response = await api.get(`/reservations/${id}`)
    return response.data
  },

  create: async (data: CreateReservationDto): Promise<Reservation> => {
    const response = await api.post('/reservations', data)
    return response.data
  },

  update: async (id: string, data: UpdateReservationDto): Promise<Reservation> => {
    const response = await api.patch(`/reservations/${id}`, data)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/reservations/${id}`)
  },

  getCalendarLinks: async (id: string): Promise<CalendarLinks> => {
    const response = await api.get(`/reservations/${id}/calendar-links`)
    return response.data
  },
}

// Subscriptions endpoints
export const subscriptionsApi = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/subscriptions')
    return response.data
  },

  getAvailable: async (): Promise<User[]> => {
    const response = await api.get('/subscriptions/available')
    return response.data
  },

  checkSubscription: async (userId: string): Promise<{ isSubscribed: boolean }> => {
    const response = await api.get(`/subscriptions/check/${userId}`)
    return response.data
  },

  subscribe: async (userId: string): Promise<UserSubscription> => {
    const response = await api.post(`/subscriptions/${userId}`)
    return response.data
  },

  unsubscribe: async (userId: string): Promise<void> => {
    await api.delete(`/subscriptions/${userId}`)
  },

  getSubscribeToAllStatus: async (): Promise<{ subscribeToAll: boolean }> => {
    const response = await api.get('/subscriptions/subscribe-to-all')
    return response.data
  },

  setSubscribeToAll: async (subscribeToAll: boolean): Promise<{ subscribeToAll: boolean; subscribedCount: number }> => {
    const response = await api.post('/subscriptions/subscribe-to-all', { subscribeToAll })
    return response.data
  },

  // Admin endpoints
  adminGetUserSubscriptions: async (userId: string): Promise<{ subscribeToAll: boolean; subscriptions: User[] }> => {
    const response = await api.get(`/subscriptions/admin/user/${userId}`)
    return response.data
  },

  adminSetUserSubscriptions: async (userId: string, subscribedToIds: string[], subscribeToAll: boolean): Promise<{ subscribeToAll: boolean; subscriptionCount: number }> => {
    const response = await api.post(`/subscriptions/admin/user/${userId}`, { subscribedToIds, subscribeToAll })
    return response.data
  },
}

// Calendar feed types
export interface CalendarFeedInfo {
  feedUrl: string
  token: string
}

// Calendar feed endpoints
export const calendarFeedApi = {
  getFeedUrl: async (): Promise<CalendarFeedInfo> => {
    const response = await api.get('/calendar/feed-url')
    return response.data
  },

  regenerateToken: async (): Promise<CalendarFeedInfo> => {
    const response = await api.post('/calendar/regenerate-token')
    return response.data
  },
}

export default api 